/**
 * Formula Tester
 * Test scoring formulas for a question
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, ArrowLeft, TestTube, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQuestion, useTestFormula } from '@/hooks/use-admin-diagnostic';

export const Route = createFileRoute('/admin/diagnostic/questions/$id/test')({
  component: FormulaTester,
});

function FormulaTester() {
  const navigate = useNavigate();
  const { id } = Route.useParams();

  const { data: question, isLoading } = useQuestion(id);
  const testFormula = useTestFormula();

  const [testValue, setTestValue] = useState<any>('');
  const [testResult, setTestResult] = useState<{ score: number; input: any } | null>(null);

  const handleTest = async () => {
    if (!question) return;

    try {
      let parsedValue: any = testValue;

      // Parse based on question type
      switch (question.type) {
        case 'scale_5':
        case 'scale_10':
        case 'numeric':
          parsedValue = parseFloat(testValue);
          break;
        case 'yesno':
          parsedValue = testValue === 'yes' ? 'yes' : 'no';
          break;
        case 'trend':
          // already a string
          break;
      }

      const result = await testFormula.mutateAsync({
        scoring_rules: question.scoring_rules,
        type: question.type,
        test_value: parsedValue,
      });

      setTestResult(result);
    } catch (error) {
      toast.error('Formula test failed: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!question) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <p className="text-destructive">Question not found</p>
      </div>
    );
  }

  const getInputField = () => {
    switch (question.type) {
      case 'scale_5':
        return (
          <Select value={testValue} onValueChange={setTestValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select value 1-5" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map((val) => (
                <SelectItem key={val} value={val.toString()}>
                  {val}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'scale_10':
        return (
          <Select value={testValue} onValueChange={setTestValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select value 1-10" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => (
                <SelectItem key={val} value={val.toString()}>
                  {val}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'yesno':
        return (
          <Select value={testValue} onValueChange={setTestValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select Yes or No" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        );

      case 'numeric':
        return (
          <Input
            type="number"
            value={testValue}
            onChange={(e) => setTestValue(e.target.value)}
            placeholder="Enter numeric value"
          />
        );

      case 'trend':
        return (
          <Select value={testValue} onValueChange={setTestValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select trend" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="up">Up (Improving)</SelectItem>
              <SelectItem value="flat">Flat (Stable)</SelectItem>
              <SelectItem value="down">Down (Declining)</SelectItem>
            </SelectContent>
          </Select>
        );

      default:
        return <Input value={testValue} onChange={(e) => setTestValue(e.target.value)} />;
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/admin/diagnostic/questions' })}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Questions
        </Button>

        <div>
          <h1 className="text-3xl font-bold">Formula Tester</h1>
          <p className="text-muted-foreground mt-1">{question.code} - Test scoring formula</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Question Info */}
        <Card>
          <CardHeader>
            <CardTitle>Question Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Question Text</Label>
              <p className="text-sm">{question.question_text}</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Type</Label>
                <p className="text-sm font-mono">{question.type}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Weight</Label>
                <p className="text-sm">{question.weight.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Pillar</Label>
                <p className="text-sm capitalize">{question.pillar?.code}</p>
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Scoring Rules</Label>
              <pre className="text-xs bg-secondary p-3 rounded-lg overflow-x-auto mt-1">
                {JSON.stringify(question.scoring_rules, null, 2)}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Test Input */}
        <Card>
          <CardHeader>
            <CardTitle>Test Input</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Enter Test Value</Label>
              {getInputField()}
              <p className="text-xs text-muted-foreground">
                {question.type === 'scale_5' && 'Select a value from 1 to 5'}
                {question.type === 'scale_10' && 'Select a value from 1 to 10'}
                {question.type === 'yesno' && 'Select Yes or No'}
                {question.type === 'numeric' && 'Enter any numeric value'}
                {question.type === 'trend' && 'Select trend direction'}
              </p>
            </div>

            <Button
              onClick={handleTest}
              disabled={!testValue || testFormula.isPending}
              className="gap-2"
            >
              {testFormula.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <TestTube className="w-4 h-4" />
                  Test Formula
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Test Result */}
        {testResult && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                Test Result
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-green-600">Input Value</Label>
                    <p className="text-2xl font-bold text-green-700">{testResult.input}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-green-600">Calculated Score</Label>
                    <p className="text-2xl font-bold text-green-700">
                      {testResult.score.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Score Bar */}
                <div className="space-y-2">
                  <Label className="text-xs text-green-600">Score Visualization</Label>
                  <div className="h-4 bg-white rounded-full overflow-hidden border border-green-200">
                    <div
                      className="h-full bg-green-500 transition-all duration-500"
                      style={{ width: `${testResult.score}%` }}
                    />
                  </div>
                  <p className="text-xs text-green-600 text-right">{testResult.score.toFixed(1)}%</p>
                </div>

                {/* Formula Explanation */}
                <div className="p-3 bg-white rounded-lg border border-green-200">
                  <Label className="text-xs text-green-600">Calculation</Label>
                  <p className="text-sm text-green-700 mt-1">
                    {question.type === 'scale_5' &&
                      `(${testResult.input} / 5) × 100 = ${testResult.score.toFixed(2)}`}
                    {question.type === 'scale_10' &&
                      `(${testResult.input} / 10) × 100 = ${testResult.score.toFixed(2)}`}
                    {question.type === 'yesno' &&
                      `Lookup: "${testResult.input}" = ${testResult.score.toFixed(2)}`}
                    {question.type === 'numeric' && `Range-based scoring: ${testResult.score.toFixed(2)}`}
                    {question.type === 'trend' &&
                      `Trend: "${testResult.input}" = ${testResult.score.toFixed(2)}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
